import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, Users, MapPin, MessageSquare } from 'lucide-react';
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { GoChevronDown } from "react-icons/go";
import axios from '../../api/axios';
import logo from '../../assets/images/SHTATO LOGO.jpg';
import image from '../../assets/images/rooftop.png';
import image1 from '../../assets/images/image2.png';
import image2 from '../../assets/images/shtattomarrakech1.jpeg';
import image3 from '../../assets/images/shtattomarrakech2.jpeg';
import image4 from '../../assets/images/shtattomarrakech3.jpeg';
import image5 from '../../assets/images/shtattomarrakech4.jpeg';

const MANAGE_URL = (token) => `/api/reservation/manage/${token}`;
const IS_AVAILABLE = "/api/reservation/available";
const images = [image, image1, image2, image3, image4, image5];
const headingStyle = { fontFamily: "Goudy Bookletter 1911, serif" };

const formatTime = (time) => {
    const hours = Math.floor(time);
    const minutes = (time % 1) * 60;
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes === 0 ? '00' : '30'}`;
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

const formatLongDate = (date) =>
    new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

const Modal = ({ open, title, children }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                {title && (
                    <h3 className="text-xl font-semibold text-[#231f20] mb-3 text-center" style={headingStyle}>
                        {title}
                    </h3>
                )}
                {children}
            </div>
        </div>
    );
};

const ManageReservation = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const autoCancel = searchParams.get('action') === 'cancel';
    const calendarRef = useRef(null);

    const [reservation, setReservation] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState('view');
    const [cancelled, setCancelled] = useState(false);
    const [randomImage, setRandomImage] = useState(images[0]);

    const [editData, setEditData] = useState({ people: '', zone: '', time: '', date: null, comments: '' });
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [fetchingTimes, setFetchingTimes] = useState(false);
    const [formError, setFormError] = useState('');
    const [suggestionMessage, setSuggestionMessage] = useState('');

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setRandomImage(images[Math.floor(Math.random() * images.length)]);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!token) {
            setLoadError('This link is invalid.');
            setLoading(false);
            return;
        }
        axios.get(MANAGE_URL(token))
            .then(res => setReservation(res.data))
            .catch(err => setLoadError(err.response?.data?.message || 'Unable to load your reservation.'))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        if (autoCancel && reservation && reservation.arrived !== 'cancelled' && !cancelled) {
            setShowCancelModal(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservation]);

    const fetchAvailableTimes = async (date, zone, currentTime) => {
        setFetchingTimes(true);
        try {
            const dateStr = date.toLocaleDateString('fr-CA');
            const response = await axios.get(IS_AVAILABLE, {
                params: { date: dateStr, zone, excludeId: reservation._id }
            });

            let times = response.data;
            const now = new Date();
            if (date.toDateString() === now.toDateString()) {
                const oneHourLater = now.getHours() + now.getMinutes() / 60 + 1;
                times = times.filter(({ time }) => time >= oneHourLater || time === currentTime);
            }
            setAvailableTimes(times);
        } catch (err) {
            setFormError('Error fetching available times.');
        } finally {
            setFetchingTimes(false);
        }
    };

    const enterEditMode = () => {
        const currentDate = new Date(reservation.date);
        setEditData({
            people: reservation.people,
            zone: reservation.zone,
            time: reservation.time,
            date: currentDate,
            comments: reservation.comments || ''
        });
        setFormError('');
        setSuggestionMessage('');
        setMode('edit');
        fetchAvailableTimes(currentDate, reservation.zone, reservation.time);
    };

    const handleDateChange = (selectedDate) => {
        setEditData(prev => ({ ...prev, date: selectedDate, time: '' }));
        setIsCalendarOpen(false);
        setSuggestionMessage('');
        fetchAvailableTimes(selectedDate, editData.zone, null);
    };

    const handleZoneChange = (e) => {
        const zone = e.target.value;
        setEditData(prev => ({ ...prev, zone, time: '' }));
        setSuggestionMessage('');
        fetchAvailableTimes(editData.date, zone, null);
    };

    const handlePeopleChange = (e) => {
        const value = parseInt(e.target.value);
        if (!value || (value >= 1 && value <= 20)) {
            setEditData(prev => ({ ...prev, people: e.target.value }));
        }
    };

    const selectTime = (time) => {
        const slot = availableTimes.find(t => t.time === time);
        if (slot?.isAvailable) {
            setEditData(prev => ({ ...prev, time }));
            setSuggestionMessage('');
        } else if (editData.zone === 'rooftop') {
            setSuggestionMessage('That time isn’t available in the rooftop. Try the salon instead.');
        }
    };

    const requestSave = () => {
        if (!editData.people || !editData.zone || editData.time === '' || !editData.date) {
            setFormError('Please fill in date, people, zone, and time.');
            return;
        }
        setFormError('');
        setShowSaveModal(true);
    };

    const confirmSave = async () => {
        setBusy(true);
        try {
            const res = await axios.put(MANAGE_URL(token), {
                ...editData,
                date: editData.date.toLocaleDateString('fr-CA')
            });
            setReservation(res.data);
            setShowSaveModal(false);
            setMode('view');
            setShowResultModal({ title: 'Updated', message: 'Your reservation has been updated.' });
        } catch (err) {
            setShowSaveModal(false);
            setShowResultModal({ title: 'Unable to Save', message: err.response?.data?.message || 'Something went wrong. Please try again.' });
        } finally {
            setBusy(false);
        }
    };

    const confirmCancel = async () => {
        setBusy(true);
        try {
            await axios.delete(MANAGE_URL(token));
            setCancelled(true);
            setShowCancelModal(false);
        } catch (err) {
            setShowCancelModal(false);
            setShowResultModal({ title: 'Unable to Cancel', message: err.response?.data?.message || 'Something went wrong. Please try again.' });
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-[#231f20]">Loading your reservation...</div>;
    }

    if (loadError) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <p className="text-center text-gray-600 max-w-sm">{loadError}</p>
            </div>
        );
    }

    if (cancelled || reservation.arrived === 'cancelled') {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center max-w-sm space-y-2">
                    <h1 className="text-3xl font-semibold text-[#231f20]" style={headingStyle}>Reservation Cancelled</h1>
                    <p className="text-gray-600">We’ve cancelled your reservation. We hope to see you another time!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-5xl mx-auto space-y-8">
            <div className="flex justify-center mt-4">
                <img src={logo} alt='Shtatto' className='w-1/5 sm:w-1/3 md:w-1/3' />
            </div>

            <div className="w-full h-[40vh] md:h-[32vh] lg:h-[26vh] overflow-hidden rounded-b-lg">
                <img src={randomImage} alt='shtatto' className='w-full h-full object-cover' />
            </div>

            <h1 className="text-4xl md:text-5xl text-[#231f20] text-center font-semibold" style={headingStyle}>
                MANAGE YOUR <br /> RESERVATION
            </h1>

            <div className="max-w-2xl mx-auto px-4 pb-16 space-y-8">
                {mode === 'view' && (
                    <div className="space-y-6">
                        <div className="border rounded-xl p-6 space-y-4 bg-white shadow-sm">
                            <div className="flex items-center gap-3 text-gray-800">
                                <CalendarIcon size={18} />
                                <span>{formatLongDate(reservation.date)}</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-800">
                                <Clock size={18} />
                                <span>{formatTime(reservation.time)}</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-800">
                                <MapPin size={18} />
                                <span className="capitalize">{reservation.zone} zone</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-800">
                                <Users size={18} />
                                <span>{reservation.people} {reservation.people == 1 ? 'guest' : 'guests'}</span>
                            </div>
                            {reservation.comments && (
                                <div className="flex items-start gap-3 text-gray-600">
                                    <MessageSquare size={18} className="mt-0.5" />
                                    <span>{reservation.comments}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={enterEditMode}
                                className="px-6 py-3 font-semibold bg-black text-white rounded-lg hover:bg-gray-800"
                            >
                                Edit Reservation
                            </button>
                            <button
                                onClick={() => setShowCancelModal(true)}
                                className="px-6 py-3 font-semibold border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
                            >
                                Cancel Reservation
                            </button>
                        </div>
                    </div>
                )}

                {mode === 'edit' && (
                    <div className="space-y-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="relative flex-1">
                                {!isCalendarOpen ? (
                                    <button
                                        type="button"
                                        onClick={() => setIsCalendarOpen(true)}
                                        className="w-full px-3 py-2 border rounded-lg flex items-center justify-between"
                                    >
                                        <div className="flex items-center">
                                            <CalendarIcon className="w-5 h-5 mr-2" />
                                            <span>{editData.date.toLocaleDateString('fr-FR')}</span>
                                        </div>
                                        <GoChevronDown color='black' />
                                    </button>
                                ) : (
                                    <div ref={calendarRef} className="absolute z-10 bg-white border rounded-lg shadow-lg">
                                        <ReactCalendar
                                            onChange={handleDateChange}
                                            value={editData.date}
                                            minDate={new Date()}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="relative flex-1">
                                <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-black" size={18} />
                                <input
                                    type="number"
                                    value={editData.people}
                                    onChange={handlePeopleChange}
                                    placeholder="People"
                                    className="w-full px-10 py-2 border rounded-lg"
                                    min={1}
                                    max={20}
                                />
                            </div>

                            <div className="relative flex-1">
                                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-black" size={18} />
                                <select
                                    value={editData.zone}
                                    onChange={handleZoneChange}
                                    className="w-full px-10 py-2 border rounded-lg bg-white appearance-none"
                                >
                                    <option value="salon">Salon</option>
                                    <option value="rooftop">Rooftop</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <p className="text-black font-semibold mb-2" style={headingStyle}>Available Time Slots</p>
                            {fetchingTimes ? (
                                <p className="text-sm text-gray-500">Loading times...</p>
                            ) : (
                                <>
                                    {suggestionMessage && <div className="text-red-600 text-sm mb-2">{suggestionMessage}</div>}
                                    <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                                        {availableTimes.map(({ time, isAvailable }) => (
                                            <button
                                                type="button"
                                                key={time}
                                                onClick={() => selectTime(time)}
                                                className={`px-2 py-3 rounded-xl text-sm
                                                    ${isAvailable
                                                        ? editData.time === time
                                                            ? 'bg-green-600 text-white'
                                                            : 'bg-[#E0E0E0] hover:bg-gray-300'
                                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    }`}
                                            >
                                                {formatTime(time)}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Comments</label>
                            <textarea
                                value={editData.comments}
                                onChange={(e) => setEditData(prev => ({ ...prev, comments: e.target.value }))}
                                rows={4}
                                maxLength={1500}
                                className="w-full px-4 py-2 text-black border border-gray-300 rounded-lg"
                            />
                            <div className="text-gray-400 text-sm text-right mt-1">{editData.comments.length}/1500</div>
                        </div>

                        {formError && <div className="text-red-600 text-sm text-center">{formError}</div>}

                        <div className="flex gap-3 justify-center pb-4">
                            <button
                                type="button"
                                onClick={() => setMode('view')}
                                className="px-6 py-3 border font-semibold border-black text-black rounded-lg"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={requestSave}
                                className="px-6 py-3 font-semibold bg-black text-white rounded-lg"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Modal open={showCancelModal} title="Cancel Reservation?">
                <p className="text-gray-600 text-center mb-6">
                    This will cancel your reservation for {formatLongDate(reservation.date)} at {formatTime(reservation.time)}. This can’t be undone.
                </p>
                <div className="flex gap-3 justify-center">
                    <button onClick={() => setShowCancelModal(false)} disabled={busy} className="px-5 py-2 border border-black rounded-lg font-semibold">
                        Keep Reservation
                    </button>
                    <button onClick={confirmCancel} disabled={busy} className="px-5 py-2 bg-red-600 text-white rounded-lg font-semibold">
                        {busy ? 'Cancelling...' : 'Yes, Cancel'}
                    </button>
                </div>
            </Modal>

            <Modal open={showSaveModal} title="Confirm Changes">
                <div className="text-gray-700 text-sm space-y-1 mb-6">
                    <p><strong>Date:</strong> {editData.date && formatLongDate(editData.date)}</p>
                    <p><strong>Time:</strong> {formatTime(editData.time)}</p>
                    <p><strong>Zone:</strong> <span className="capitalize">{editData.zone}</span></p>
                    <p><strong>People:</strong> {editData.people}</p>
                    {editData.comments && <p><strong>Comments:</strong> {editData.comments}</p>}
                </div>
                <div className="flex gap-3 justify-center">
                    <button onClick={() => setShowSaveModal(false)} disabled={busy} className="px-5 py-2 border border-black rounded-lg font-semibold">
                        Go Back
                    </button>
                    <button onClick={confirmSave} disabled={busy} className="px-5 py-2 bg-black text-white rounded-lg font-semibold">
                        {busy ? 'Saving...' : 'Confirm'}
                    </button>
                </div>
            </Modal>

            <Modal open={!!showResultModal} title={showResultModal?.title}>
                <p className="text-gray-600 text-center mb-6">{showResultModal?.message}</p>
                <div className="flex justify-center">
                    <button onClick={() => setShowResultModal(null)} className="px-6 py-2 bg-black text-white rounded-lg font-semibold">
                        Close
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default ManageReservation;